import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Action = { id: string; action_text: string; due_on?: string | null; status: string };

type Meeting = {
  id: string;
  committee_id: string;
  meeting_no: string;
  meeting_on: string;
  agenda?: string | null;
  minutes_text?: string | null;
  status: string;
  record_version: number;
  committee_name?: string;
  committee_code?: string;
  actions: Action[];
};

export default function GovernanceMeetingDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.governance.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-governance-meeting', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Meeting>(`/education/governance/meetings/${id}`)).data,
  });
  const row = query.data;
  const open = row && row.status !== 'cancelled';

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['education-governance-meeting', id] });
    if (row?.committee_id) {
      queryClient.invalidateQueries({ queryKey: ['education-governance-committee', row.committee_id] });
    }
  };

  const saveMinutes = useMutation({
    mutationFn: (values: { minutes_text: string; agenda?: string }) =>
      api.post(`/education/governance/meetings/${id}/minutes`, {
        ...values,
        status: 'held',
        record_version: row?.record_version,
      }),
    onSuccess: () => {
      message.success(t('meetingMinutesSaved'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addAction = useMutation({
    mutationFn: (values: { action_text: string; due_on?: dayjs.Dayjs }) =>
      api.post(`/education/governance/meetings/${id}/actions`, {
        action_text: values.action_text,
        due_on: values.due_on ? values.due_on.format('YYYY-MM-DD') : undefined,
      }),
    onSuccess: () => {
      message.success(t('committeeActionAdded'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const actionStatus = useMutation({
    mutationFn: ({ actionId, status }: { actionId: string; status: string }) =>
      api.post(`/education/governance/actions/${actionId}/status`, { status }),
    onSuccess: () => {
      message.success(t('committeeActionUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={row?.meeting_no || t('meetings')}
      description={t('governanceCommitteesIntro')}
      tags={row ? <Tag>{row.status}</Tag> : null}
      extra={
        <Button
          onClick={() =>
            navigate(
              row?.committee_id
                ? `/education/governance/committees/${row.committee_id}`
                : '/education/governance/committees'
            )
          }
        >
          {t('governanceCommittees')}
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('governanceMinutesHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('committee')}>
                {row.committee_code} — {row.committee_name}
              </Descriptions.Item>
              <Descriptions.Item label={t('meetingOn')}>{row.meeting_on}</Descriptions.Item>
              <Descriptions.Item label={t('agenda')} span={2}>
                {row.agenda || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('minutes')} span={2}>
                {row.minutes_text || '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>

        {canManage && open ? (
          <Card size="small" title={t('saveMinutes')}>
            <Form
              layout="vertical"
              style={{ maxWidth: 640 }}
              initialValues={{
                agenda: row?.agenda || undefined,
                minutes_text: row?.minutes_text || undefined,
              }}
              onFinish={(values) => saveMinutes.mutate(values)}
            >
              <Form.Item name="agenda" label={t('agenda')}>
                <Input.TextArea rows={2} maxLength={1000} />
              </Form.Item>
              <Form.Item name="minutes_text" label={t('minutes')} rules={[{ required: true }]}>
                <Input.TextArea rows={6} maxLength={4000} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={saveMinutes.isPending}>
                {t('saveMinutesAsHeld')}
              </Button>
            </Form>
          </Card>
        ) : null}

        {canManage && open ? (
          <Card size="small" title={t('committeeActions')}>
            <Form
              layout="vertical"
              style={{ maxWidth: 560, marginBottom: 16 }}
              onFinish={(values) => addAction.mutate(values)}
            >
              <Form.Item name="action_text" label={t('action')} rules={[{ required: true }]}>
                <Input.TextArea rows={2} maxLength={500} />
              </Form.Item>
              <Form.Item name="due_on" label={t('dueOn')}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addAction.isPending}>
                {t('addAction')}
              </Button>
            </Form>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={row?.actions || []}
              columns={[
                { title: t('action'), dataIndex: 'action_text' },
                { title: t('dueOn'), dataIndex: 'due_on', width: 120, render: (v) => v || '—' },
                {
                  title: t('status'),
                  dataIndex: 'status',
                  width: 100,
                  render: (v: string) => <Tag>{v}</Tag>,
                },
                {
                  title: t('actions'),
                  width: 180,
                  render: (_: unknown, action: Action) =>
                    action.status === 'open' ? (
                      <Space>
                        <Button
                          size="small"
                          onClick={() => actionStatus.mutate({ actionId: action.id, status: 'done' })}
                        >
                          {t('done')}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => actionStatus.mutate({ actionId: action.id, status: 'cancelled' })}
                        >
                          {t('statusCancelled')}
                        </Button>
                      </Space>
                    ) : null,
                },
              ]}
            />
          </Card>
        ) : row?.actions?.length ? (
          <Card size="small" title={t('committeeActions')}>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={row.actions}
              columns={[
                { title: t('action'), dataIndex: 'action_text' },
                { title: t('dueOn'), dataIndex: 'due_on', width: 120, render: (v) => v || '—' },
                {
                  title: t('status'),
                  dataIndex: 'status',
                  width: 100,
                  render: (v: string) => <Tag>{v}</Tag>,
                },
              ]}
            />
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
